import { LitElement, html, css } from "lit"
import { classMap } from "lit/directives/class-map.js"

class HHButton extends LitElement {
  static properties = {
    key: {},
    href: { type: String },
    decoration: { type: String },
    spacing: { type: String },
    type: { type: String },
    disabled: {
      type: Boolean,
      reflect: true,
    },
    underline: {
      converter(value) {
        return value === "true" ? true : false
      },
    },
    onclick: {
      type: String,
      attribute: "custom:click",
    },
  }

  constructor() {
    super()

    this.underline = true
    this.disabled = false
  }

  onClick(e) {
    if (!this.onclick) {
      return
    }

    this.dispatchEvent(
      new CustomEvent(this.onclick, {
        bubbles: true,
        composed: true,
        detail: {
          key: this.key,
          event: e,
        },
      })
    )
  }

  render() {
    return html`${!this.href
      ? html`<button
          class="${classMap({
            underline: this.underline,
          })}"
          ?disabled="${this.disabled}"
          @click="${this.onClick}"
          type="${this.type || "button"}"
          value="Submit"
        >
          <slot></slot>
        </button>`
      : html`<a
          class="${classMap({
            underline: this.underline,
          })}"
          href="${this.href}"
          @click="${this.onClick}"
        >
          <slot></slot>
        </a>`}`
  }

  static styles = css`
    button {
      min-width: var(--hh-min-width, 16rem);
    }

    :host([width="full"]) button,
    :host([width="full"]) a {
      width: 100%;
      min-width: none;
    }

    :host([height="full"]) button,
    :host([height="full"]) a {
      height: 100% !important;
    }

    :host([theme="tertiary"]) button {
      min-width: none;
    }

    button,
    a {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      background-color: var(--hh-background, #012e36);
      color: var(--hh-color, #f6f4f0);
      transition: background var(--hh-duration-default, 300ms),
        color var(--hh-duration-default, 300ms),
        border var(--hh-duration-default, 300ms);
      border: 0.1rem solid var(--hh-background, #012e36);
      appearance: none;
      font: var(--hh-font);
      text-transform: var(--hh-text-transform, uppercase);
      letter-spacing: var(--hh-letter-spacing, 0.48px);
      padding: var(--hh-padding, 1rem 2rem);
      text-decoration: none;
      height: 5rem;
      box-sizing: border-box;
      gap: var(--hh-gap, 1rem);
    }

    @media screen and (min-width: 1920px) {
      button,
      a {
        height: max(calc(50 / var(--screen-size) * 100vw), 5rem);
      }
    }

    button:hover,
    a:hover {
      background-color: var(--hh-color, #f6f4f0);
      color: var(--hh-text-color, #000);
    }

    button:disabled,
    a[href=""] {
      background-color: var(--hh-disabled-bg-color, #eae6e3);
      border-color: var(--hh-disabled-bg-color, #eae6e3);
      color: var(--hh-color, #f6f4f0);
      cursor: not-allowed;
    }

    button:focus-visible,
    a:focus-visible {
      outline: 0.2rem solid var(--hh-color-focus, #0038ff);
      outline-offset: 0 !important;
    }

    :host([theme="secondary"]) button,
    :host([theme="secondary"]) a {
      background: var(--hh-background, transparent);
      border: var(--hh-border-width, 0.1rem) solid var(--hh-border-color, #000);
      color: var(--hh-secondary-color, #666666);
    }

    :host([theme="secondary"]) button:hover,
    :host([theme="secondary"]) a:hover {
      background: var(--hh-text-color, #000);
      border-color: var(--hh-text-color, #000);
      color: var(--hh-color, #f6f4f0);
    }

    :host([theme="secondary"]) button:disabled,
    :host([theme="secondary"]) a[href=""] {
      background: var(--hh-color, #f6f4f0);
      border-color: var(--hh-disabled-bg-color, #eae6e3);
      color: var(--hh-disabled-bg-color, #eae6e3);
    }

    :host([theme="secondary"]) button:focus-visible,
    :host([theme="secondary"]) a:focus-visible {
      background: var(--hh-color, #f6f4f0);
      color: var(--hh-text-color, #000);
    }

    :host([theme="tertiary"]) button,
    :host([theme="tertiary"]) a {
      position: relative;
      background: var(--hh-background, transparent);
      color: var(--hh-text-color, #000);
      padding: var(--hh-padding, 0) var(--hh-padding, 0)
        var(--hh-border-bottom-offset, 0);
      border: 0;
      min-width: unset;
      height: auto;
    }

    :host([theme="tertiary"]) button:disabled,
    :host([theme="tertiary"]) a[href=""] {
      color: var(--hh-disabled-bg-color, #eae6e3);
    }

    :host([theme="tertiary"]) button:disabled:after,
    :host([theme="tertiary"]) a[href=""]:after {
      background-color: var(--hh-disabled-bg-color, #eae6e3);
      height: var(--hh-underline-width, 0.1rem);
    }

    :host([theme="tertiary"]:not([focusable="true"])) button:focus-visible,
    :host([theme="tertiary"]:not([focusable="true"])) a:focus-visible {
      border: 0;
      outline: none !important;
    }

    :host([theme="tertiary"]) button:focus-visible:after,
    :host([theme="tertiary"]) a:focus-visible:after {
      height: calc(var(--hh-underline-width, 0.1rem) * 2);
      background-color: var(--hh-color-focus, #0038ff);
    }

    :host([theme="tertiary"].anim) button:after,
    :host([theme="tertiary"].anim) a:after {
      content: var(--hh-btn-underline-content, none);
      position: absolute;
      bottom: 0;
      left: 0;
      width: 0;
      height: var(--hh-underline-width, 0.1rem);
      background-color: var(--hh-text-color, #000);
      opacity: 0;
      transition: all var(--hh-duration-default, 300ms);
    }

    :host([theme="tertiary"].anim) button:hover:after,
    :host([theme="tertiary"].anim) a:hover:after {
      rigth: 0;
      opacity: 1;
      width: 100%;
    }

    :host([theme="tertiary"].anim--appear) button:after,
    :host([theme="tertiary"].anim--appear) a:after {
      height: var(--hh-underline-width, 0.1rem);
      background-color: var(--hh-text-color, #000);
      width: 0;
      opacity: 0;
    }

    :host([theme="tertiary"].anim--appear) button:hover:after,
    :host([theme="tertiary"].anim--appear) a:hover:after {
      width: calc(100% - var(--hh-letter-spacing, 0));
      opacity: 1;
    }

    :host([theme="tertiary"][show-underline]) button:after,
    :host([theme="tertiary"][show-underline]) a:after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: var(--hh-underline-width, 0.1rem);
      background-color: var(--hh-text-color, #000);
      opacity: 1;
    }

    :slotted() {
      font: var(--hh-font);
    }

    :host([decoration~="rounded"]) button,
    :host([decoration~="rounded"]) a {
      border-radius: var(--hh-border-radius, 0.4rem);
    }
  `
}

customElements.define("hh-button", HHButton)
